import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosResponse } from "axios";
import { getCacheFriendlyInstruction } from "components/filters/Filters/InstructionHelpers";
import { useApi, useCustomQuery } from "utilities/hookutils";

// TODO: All this file should be migrated to components/Tables/MySelection
const listShowQueryKey = "listShow";

export const useFetchShowsApi = (instruction: server.dto.PaginatedShowInternalInstruction, onSuccess: (res: AxiosResponse<server.dto.ApiResponseWrapperWithRequest<server.dto.ShowForListInternal[], server.dto.ListShowInternalFilters>>) => void) => {
  const cacheFriendlyInstruction = getCacheFriendlyInstruction(instruction);

  return useCustomQuery({
    queryFn: async () => {
      return window.CoreApi.Show.PaginatedInternalEnvironment(cacheFriendlyInstruction);
    },
    queryKey: ["listShow", instruction],
    onSuccess,
    cacheTime: 0
  });
};

const useMutateAndInvalidate = <T>(mutationFn: (...args: T[]) => Promise<AxiosResponse<server.dto.ApiResponseWrapper<string> | server.dto.ApiResponse, any>>, queryKeys: string[]) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => queryKeys.some(q => query.queryKey.includes(q))
      });
    },
    retry: false
  });
};

export const useBatchAddTags = (mainArea: server.dto.MainAreas) => {
  return useMutateAndInvalidate(async (tagIds: number[]) => window.CoreApi.MySelection.BatchChangeTags({
    tagIds,
    mainArea,
    action: "addTags"
  }), [listShowQueryKey]);
};

export const useBatchChangeOwner = (mainArea: server.dto.MainAreas) => {
  return useMutateAndInvalidate(async (ownerId: number) => window.CoreApi.MySelection.BatchChangeOwner({
    userId: ownerId,
    mainArea
  }), [listShowQueryKey]);
};

export const useBatchChangeVisibleToExternal = (mainArea: server.dto.MainAreas) => {
  return useMutateAndInvalidate(async (visibleToExternal: boolean) => window.CoreApi.MySelection.BatchChangeVisibleToExternal({
    mainArea,
    visibleToExternal,
  }), [listShowQueryKey]);
}

export const useBatchChangePublication = () => {
  return useMutateAndInvalidate(async (publication: server.dto.PublicationStatuses) => window.CoreApi.MySelection.BatchSetPublication({
    publication
  }), [listShowQueryKey]);
};

export const useBatchChangeStatus = (mainArea: server.dto.MainAreas) => {
  return useMutateAndInvalidate(async (statusId: number) => window.CoreApi.MySelection.BatchChangeStatus({
    statusId,
    mainArea
  }), [listShowQueryKey]);
};

export const useBatchDelete = (mainArea: server.dto.MainAreas) => {
  return useMutateAndInvalidate(async (_: any) => window.CoreApi.MySelection.BatchDelete({
    mainArea
  }), [listShowQueryKey]);
};

export const useBatchReplaceTags = (mainArea: server.dto.MainAreas) => {
  return useMutateAndInvalidate(async (tagIds: number[]) => window.CoreApi.MySelection.BatchChangeTags({
    tagIds,
    mainArea,
    action: "replaceTags"
  }), [listShowQueryKey]);
};

const mySelectionCountQueryKey = "mySelectionCount";

export const useMySelectionCount = (mainArea: server.dto.MainAreas) => {
  return useApi(
    async () => window.CoreApi.MySelection.Count(mainArea),
    [mySelectionCountQueryKey]
  );
};

export const useClearMySelectionCount = () => {
  return useMutateAndInvalidate(async (mainArea: server.dto.MainAreas) => window.CoreApi.MySelection.ClearAll(mainArea), [mySelectionCountQueryKey]);
};

export const useRemoveSelection = (mainArea: server.dto.MainAreas) => {
  return useMutateAndInvalidate(async (ids: number[]) => window.CoreApi.MySelection.Remove({ ids, mainArea }), [mySelectionCountQueryKey]);
};

export const useAddSelection = (mainArea: server.dto.MainAreas) => {
  return useMutateAndInvalidate(async (ids: number[]) => window.CoreApi.MySelection.Add({ ids, mainArea }), [mySelectionCountQueryKey]);
};